// src/app/components/foundation/Modal.tsx
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

import './Modal.css';

export interface ModalProps {
  /** Controls mounting. Renders nothing (no DOM node at all) when false. */
  open: boolean;
  /** Called on backdrop click, Escape key, or the header close button. */
  onClose: () => void;
  /** Optional header title; omit for a header-less dialog. */
  title?: ReactNode;
  children: ReactNode;
  /** Optional footer row (e.g. action buttons), rendered below children. */
  footer?: ReactNode;
  className?: string;
}

/**
 * Minimal glass-morphism modal/dialog primitive.
 *
 * There was no Modal/Dialog anywhere in app/components/foundation
 * before this feature (checked foundation/index.ts, Card.tsx,
 * PageHeader.tsx, PageContainer.tsx, EmptyState.tsx — none of them
 * cover an overlay/dialog). Built here following the exact surface
 * convention Card already uses (the shared `vania-glass-card` class),
 * so the dialog panel matches every other elevated surface in the app
 * instead of introducing a new visual language.
 *
 * No new UI library and no new dependency: the only new import is
 * `createPortal` from `react-dom`, which already ships with React
 * itself in this app.
 *
 * Presentation + open/close mechanics only — callers own the `open`
 * state and whatever happens on close/submit.
 */
export function Modal({ open, onClose, title, children, footer, className }: ModalProps) {
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="vania-modal-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        className={clsx('vania-modal-panel', 'vania-glass-card', className)}
      >
        {title && (
          <div className="vania-modal-header">
            <h2 className="vania-modal-title">{title}</h2>
            <button
              type="button"
              className="vania-modal-close"
              onClick={onClose}
              aria-label="بستن"
            >
              ×
            </button>
          </div>
        )}

        <div className="vania-modal-body">{children}</div>

        {footer && <div className="vania-modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
